import React, { useState, useRef, useEffect } from "react";
import { createPortal } from 'react-dom';
import {DropdownStyled, DropdownMenuLayer} from "../../../styled/Dropdown";
import {useTheme} from "../Theme/ThemeContext";

const Dropdown = ({ trigger, items = [], onSelect, menuClassName, forceDark = false }) => {
    const {themeName} = useTheme();
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);
    const itemsRefs = useRef([]);
    const triggerRef = useRef(null);
    const [menuPos, setMenuPos] = useState({ top: 0, right: 0, caretOffset: 16 });
    const menuRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            const inTrigger = dropdownRef.current && dropdownRef.current.contains(e.target);
            const inMenu = menuRef.current && menuRef.current.contains(e.target);
            if (!inTrigger && !inMenu) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Keyboard navigation and a11y
    useEffect(() => {
        const el = dropdownRef.current;
        if (!el) return;
        const onKeyDown = (e) => {
            if (!open) return;
            const idx = itemsRefs.current.findIndex(r => r === document.activeElement);
            if (e.key === 'Escape') { setOpen(false); }
            else if (e.key === 'ArrowDown') {
                e.preventDefault();
                const next = Math.min(items.length - 1, Math.max(0, idx + 1));
                itemsRefs.current[next]?.focus();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                const prev = Math.max(0, idx - 1);
                itemsRefs.current[prev]?.focus();
            }
        };
        el.addEventListener('keydown', onKeyDown);
        return () => el.removeEventListener('keydown', onKeyDown);
    }, [open, items.length]);

    // Compute fixed coordinates for the menu to avoid stacking context clipping
    useEffect(() => {
        if (!open) return;
        const updatePos = () => {
            try {
                const rect = triggerRef.current?.getBoundingClientRect();
                if (!rect) return;
                setMenuPos({
                    top: Math.round(rect.bottom + 8),
                    right: Math.max(0, Math.round(window.innerWidth - rect.right)),
                    caretOffset: Math.round(rect.width / 2)
                });
            } catch (_) {}
        };
        updatePos();
        window.addEventListener('resize', updatePos);
        window.addEventListener('scroll', updatePos, true);
        return () => {
            window.removeEventListener('resize', updatePos);
            window.removeEventListener('scroll', updatePos, true);
        };
    }, [open]);

    return (
        <DropdownStyled ref={dropdownRef} theme={themeName} $forceDark={forceDark}>
            <div
                className="dropdown-trigger"
                role="button"
                aria-haspopup="menu"
                aria-expanded={open}
                tabIndex={0}
                ref={triggerRef}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(v => !v); }
                }}
                onClick={() => setOpen(!open)}
            >
                {trigger}
            </div>

                                            {open && createPortal(
                                                        <DropdownMenuLayer
                                                            className={`dropdown-menu ${menuClassName || ''}`}
                                                role="menu"
                                                ref={menuRef}
                                                theme={themeName}
                                                $forceDark={forceDark}
                                                style={{ top: `${menuPos.top}px`, right: `${menuPos.right}px`, '--caret-offset': `${menuPos.caretOffset}px` }}
                                            >
                                    {items.map((item, index) => (
                                        <li
                                            key={index}
                                            className={`dropdown-item ${item.className || ''}`}
                                            role="menuitem"
                                            tabIndex={0}
                                            ref={el => itemsRefs.current[index] = el}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.(item); setOpen(false); }
                                            }}
                                            onClick={() => {
                                                onSelect?.(item);
                                                setOpen(false);
                                            }}
                                        >
                                            {item.label}
                                        </li>
                                    ))}
                                            </DropdownMenuLayer>,
                                document.body
                        )}
        </DropdownStyled>
    );
};

export default Dropdown;